const TeametAbout = () => {
  const teamMembers = [
    {
      name: "Dr. Inna Tolskaya",
      role: "Learning & Communication",
      subtitle: "Pronunciation & Teaching",
      image: "it.jpg",
      experience: "Passionate about helping learners find their voice in new languages. Focuses on creating supportive learning environments where pronunciation practice feels natural and empowering.",
      projects: [
        { name: "EasyRights", url: "https://www.easyrights.eu/" },
        { name: "Capeesh", url: "https://www.capeesh.com/" }
      ]
    },
    {
      name: "Dr. Dmitry Shcherbin",
      role: "Technology & Development",
      subtitle: "Building Learning Tools",
      image: "ds.jpg",
      experience: "Loves creating technology that actually helps people learn. Builds tools that listen carefully to how you speak and give helpful feedback on your pronunciation journey.",
      projects: [
        { name: "CALST", url: "https://www.ntnu.edu/isl/calst" },
        { name: "Pygmalion", url: "https://pygmalion.hf.ntnu.no/" },
        { name: "EasyRights", url: "https://www.easyrights.eu/" }
      ]
    },
    {
      name: "Associate professor Olaf Husby",
      role: "Language & Research",
      subtitle: "Sounds & Teaching Methods",
      image: "oh.jpg",
      experience: "Dedicated to understanding how people learn pronunciation. Works on making language learning more effective by focusing on the rhythm and music of speech.",
      projects: [{ name: "NOW: Norwegian on the Web 1 ", url: "https://www.ntnu.edu/now/" },
        { name: "NOW2: Norwegian on the Web 2", url: "https://www.ntnu.edu/now2" },
        { name: "LIM", url: "https://www.ntnu.edu/isl/lim" },
        { name: "CALST", url: "https://www.ntnu.edu/isl/calst" },
        { name: "L1-L2 Map", url: "https://l1-l2map.it.ntnu.no/" },
        { name: "Språksnakk, NRK P2", url: "https://radio.nrk.no/podkast/spraakteigen" }
      ]
    }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:p-8 space-y-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 font-mono mb-2">
          Meet Our Team
        </h1>
        <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        <p className="text-gray-600 mt-4 text-lg">
          Language learning enthusiasts helping you master pronunciation
        </p>
      </div>

      {/* Team Members Grid */}
      <div className="grid lg:grid-cols-3 gap-8">
        {teamMembers.map((member, index) => (
          <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 space-y-6 hover:shadow-lg transition-all duration-300">
            {/* Profile Image & Name */}
            <div className="text-center">
              <div className="relative group">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl">
                  <img 
                    src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/${member.image}`}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                {/* Subtle glow effect on hover */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-200 to-purple-200 opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10"></div>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors duration-300">{member.name}</h3>
              <p className="text-sm text-blue-600 font-medium mb-1">{member.role}</p>
              <p className="text-xs text-gray-600">{member.subtitle}</p>
            </div>

            {/* Approach Section */}
            <section className="space-y-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3">💫</div>
                <h3 className="text-lg font-semibold text-gray-800">My Focus</h3>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-gray-600 leading-relaxed text-sm">
                  {member.experience}
                </p>
              </div>
            </section>

            {/* Projects Section */}
            <section className="space-y-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-3">✨</div>
                <h3 className="text-lg font-semibold text-gray-800">Selected Projects</h3>
              </div>
              <div className="space-y-2">
                {member.projects.map((project, projectIndex) => (
                  <a
                    key={projectIndex}
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white rounded-lg p-3 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-800 font-medium group-hover:text-blue-600 transition-colors text-sm">
                        {project.name}
                      </span>
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                        <svg className="w-3 h-3 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          </div>
        ))}
      </div>

      {/* What We Believe Section */}
      <section className="space-y-6">
        <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-purple-500 pl-4">
          What We Believe
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200 text-center hover:shadow-md transition-all duration-300 hover:transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">🎵</div>
            <h4 className="font-semibold text-gray-800 mb-3">It's About Rhythm</h4>
            <p className="text-sm text-gray-600">
              Good pronunciation isn't just about sounds—it's about the music and flow of your speech
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 text-center hover:shadow-md transition-all duration-300 hover:transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">🌱</div>
            <h4 className="font-semibold text-gray-800 mb-3">Practice Makes Progress</h4>
            <p className="text-sm text-gray-600">
              Everyone improves with regular practice and helpful feedback
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200 text-center hover:shadow-md transition-all duration-300 hover:transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">😊</div>
            <h4 className="font-semibold text-gray-800 mb-3">Confidence Matters</h4>
            <p className="text-sm text-gray-600">
              Feeling good about how you sound helps you communicate more freely
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200 hover:shadow-md transition-all duration-300">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">💭</div>
            <p className="text-gray-700 flex-1">
              <span className="font-semibold">Our approach:</span> We're here to help you sound more like yourself in your new language. 
              It's not about perfection—it's about clear, confident communication that feels good to you.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeametAbout;